/*
 * A vigência do período do Catálogo de Experiências, declarada em `periodo_catalogo__c`.
 *
 * A coleção diz três coisas por ciclo: quando abre (`startDate`), quando fecha (`endDate`) e se
 * está ligado (`active`). Até esta feature só o `endDate` decidia alguma coisa, e só no navegador.
 * A trava de verdade passou a morar nas triggers `E1djhtF` e `FnEN39m`; este módulo é a metade do
 * front, e cuida apenas do que o jogador VÊ.
 *
 * `active` não aparece aqui de propósito (decisão do usuário, 2026-09-08): desligar o catálogo é
 * uma chave do servidor, e o front só precisa saber contar até a abertura.
 */

/** O que o documento do período declara, já convertido. Campo ausente = não declarado. */
export interface CatalogPeriod {
  start?: Date;
  end?: Date;
}

/**
 * Onde o "agora" cai em relação à janela do ciclo.
 *
 * `desconhecido` é um estado próprio, e não um sinônimo de `antes`: sem `startDate` não existe
 * data para contar, e anunciar uma abertura que ninguém marcou é pior que não anunciar nada. O
 * 2S2026 está exatamente nesse estado — ciclo sem documento em `periodo_catalogo__c`.
 */
export type CatalogPeriodState = 'antes' | 'aberto' | 'encerrado' | 'desconhecido';

/**
 * A data de um campo do documento, seja qual for a forma em que ela chegou.
 *
 * A API devolve `{ "$date": "2026-09-18T22:59:00.000Z" }`; um documento gravado à mão pode trazer
 * o mesmo campo em milissegundos, e um chamador pode já ter convertido. As três formas viram
 * `Date`, e tudo o mais vira `undefined`.
 *
 * Data inválida também sai como `undefined`. `new Date('abc')` devolve um objeto que passa em
 * qualquer teste de existência e só falha ao renderizar, como "NaN" — o chamador não deveria
 * precisar lembrar disso.
 */
export function parsePeriodDate(raw: unknown): Date | undefined {
  const bruto = raw && typeof raw === 'object' && '$date' in raw ? (raw as { $date: unknown }).$date : raw;

  if (bruto instanceof Date) {
    return isNaN(bruto.getTime()) ? undefined : bruto;
  }

  if (typeof bruto !== 'string' && typeof bruto !== 'number') {
    return undefined;
  }

  const data = new Date(bruto);

  return isNaN(data.getTime()) ? undefined : data;
}

/**
 * Os dois limites do período, lidos de um documento de `periodo_catalogo__c`.
 *
 * A abertura vem de `startDate` e de mais nada. O front usava `created` como segunda opção, e os
 * dois campos não são a mesma coisa: no documento do 1S2026, `created` é 14/08 e `startDate` é
 * 13/08. Enquanto o `startDate` só alimentava a barra de progresso a diferença passava batida;
 * agora ela é a data que o jogador lê na contagem regressiva.
 */
export function readCatalogPeriod(doc?: Record<string, unknown> | null): CatalogPeriod {
  return {
    start: parsePeriodDate(doc?.startDate),
    end: parsePeriodDate(doc?.endDate),
  };
}

/**
 * Onde o instante cai na janela do ciclo.
 *
 * As duas pontas são inclusivas porque o servidor é inclusivo: a `DsfRsg1` recusa com
 * `agora > endDate` e a guarda nova das triggers espelha isso no início. Um front exclusivo
 * esconderia o catálogo no primeiro milissegundo do período e ofereceria o botão no último.
 *
 * O `agora` é sempre o relógio do SERVIDOR (`getServerTime()`), nunca `Date.now()` — o mesmo
 * critério de `canUndoPurchase`. Navegador atrasado abriria a vitrine antes da hora; adiantado
 * esconderia um catálogo aberto.
 */
export function resolveCatalogPeriodState(now: number, period: CatalogPeriod): CatalogPeriodState {
  const { start, end } = period;

  if (!start) return 'desconhecido';
  if (now < start.getTime()) return 'antes';
  if (end && now > end.getTime()) return 'encerrado';

  return 'aberto';
}
